import {
  WorkspaceRoutine,
  useAllWorkspaces,
  useCreateMutation,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  Colors,
  Layout,
  SaveFormTop,
  Select,
  TopModalBar,
  TouchableButton,
} from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, StyleSheet, View } from 'react-native';

type CopyRoutineMenuItemProps = {
  workspaceRoutine: WorkspaceRoutine;
  onSelected: () => void;
};

export const CopyToWorkspaceButton = (props: CopyRoutineMenuItemProps) => {
  const { t } = useTranslation();
  const data = useAllWorkspaces();
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const workspace = useCurrentWorkspace();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<
    string | undefined
  >();
  const [showModal, setShowModal] = useState(false);
  const { mutateAsync } = useCreateMutation({
    modelName: 'WorkspaceRoutine',
    query: COPY_ROUTINE,
  });

  useEffect(() => {
    if (workspace) {
      setSelectedWorkspaceId(workspace.id);
    }
  }, [workspace]);

  useEffect(() => {
    if (!data) return;

    setWorkspaces(
      data
        .filter((x) => x.id !== props.workspaceRoutine.workspace?.id)
        .map((w) => ({
          label: w.name,
          value: w.id,
        }))
    );
  }, [data, props.workspaceRoutine.workspace?.id]);

  const onCancel = () => {
    setSelectedWorkspaceId(workspace?.id);
    setShowModal(false);
  };

  const onSubmit = async () => {
    const routineToCreate = {
      routineIds: [props.workspaceRoutine.id],
      targetWorkspaceId: selectedWorkspaceId,
    };

    await mutateAsync({ input: routineToCreate });

    setShowModal(false);
    props.onSelected();
  };

  return (
    <>
      <TouchableButton
        onPress={() => setShowModal(true)}
        icon="content-copy"
        color={Colors.blackText}
        iconSize={16}
        label={t('routines.menu.copyToWorkspace')}
        style={styles.button}
      />
      <Modal visible={showModal} onRequestClose={onCancel} transparent>
        <View style={styles.modalContainer}>
          <View style={styles.container}>
            <SaveFormTop
              topBar={TopModalBar}
              isValid={!!selectedWorkspaceId}
              saveLabel={t('routines.copyToWorkspace.save')}
              title={t('routines.copyToWorkspace.title')}
              onCancel={onCancel}
              onSubmit={onSubmit}
            >
              <View style={styles.form}>
                <Select
                  label={t('routines.copyToWorkspace.workspace')}
                  items={workspaces}
                  onSelectItem={(item) => setSelectedWorkspaceId(item)}
                  searchable
                  searchPlaceholder={t('routines.copyToWorkspace.workspace')}
                  dropDownPickerProps={{
                    value: selectedWorkspaceId,
                  }}
                />
              </View>
            </SaveFormTop>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 7,
    backgroundColor: Colors.blackInactive,
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 560,
  },
  form: {
    ...Layout.container,
    width: '100%',
    maxWidth: 560,
    maxHeight: 130,
    backgroundColor: Colors.primaryGrayBackground,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    padding: 20,
  },
  button: {
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.white,
    marginRight: 20,
    borderRadius: 80,
    paddingHorizontal: 10,
  },
});

const COPY_ROUTINE = `
  mutation CopyWorkspaceRoutinesToWorkspace($input: CopyWorkspaceRoutinesToWorkspaceInput!) {
    routine {
      copyWorkspaceRoutinesToWorkspace(input: $input) {
        id
        name
      }
    }
  }
`;
